import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Wand2, Loader2, AlertTriangle, Zap, ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import UpgradeDialog from '../components/jixxeo/UpgradeDialog';
import CreationLightbox from '../components/jixxeo/CreationLightbox';
import { inspectPrompt } from '../lib/promptGuard';
import PromptGuardWarning from '../components/jixxeo/PromptGuardWarning';

export default function ImageStudio() {
  const { user, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState('1:1');
  const [style, setStyle] = useState('photorealistic');
  const [negative, setNegative] = useState('');
  const [n, setN] = useState(1);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [usage, setUsage] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [guardAck, setGuardAck] = useState(false);
  const guard = inspectPrompt(prompt);
  // Reset the ack whenever the prompt changes so the user re-reviews.
  useEffect(() => { setGuardAck(false); }, [prompt]);

  const loadUsage = async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/ai/image/usage');
      setUsage(data);
    } catch {}
  };
  useEffect(() => { loadUsage(); }, [user]);

  // Pre-fill prompt when arriving from Edit/Regenerate
  useEffect(() => {
    const fromUrl = searchParams.get('prefill');
    if (fromUrl) {
      setPrompt(decodeURIComponent(fromUrl));
      return;
    }
    try {
      const raw = sessionStorage.getItem('jixxeo_image_prefill');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.prompt) setPrompt(parsed.prompt);
        sessionStorage.removeItem('jixxeo_image_prefill');
      }
    } catch {}
  }, [searchParams]);

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    if (!user) { toast.error('Sign in to generate'); return; }
    const check = inspectPrompt(prompt);
    if (!check.ok && !guardAck) {
      toast.warning('Prompt may trip the safety filter — review the warning and tap Generate again to proceed.');
      return;
    }
    setLoading(true);
    setErrorMsg('');
    try {
      // Fire-and-poll: /ai/image/generate returns a job_id immediately. We poll
      // /ai/image/jobs/{job_id} every 2s so Cloudflare never times out on a slow
      // Nano Banana request. Retry the initial POST up to 3 times on transient
      // 5xx/network errors (Cloudflare 502s on cold origin).
      let start = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data } = await api.post('/ai/image/generate',
            { prompt, aspect_ratio: aspect, style, n, negative },
            { timeout: 15_000 });
          start = data;
          break;
        } catch (postErr) {
          const code = postErr?.response?.status;
          const isRetriable = !code || code >= 500 || code === 408 || code === 429;
          if (attempt === 3 || !isRetriable) throw postErr;
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
      if (!start?.job_id) throw new Error('No job id returned');
      const jobId = start.job_id;
      const deadline = Date.now() + 180_000; // 3 min hard cap
      let latestImages = [];
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 2000));
        let job;
        try {
          const { data } = await api.get(`/ai/image/jobs/${jobId}`);
          job = data;
        } catch {
          continue; // transient — keep polling
        }
        if (Array.isArray(job.images) && job.images.length > latestImages.length) {
          const newest = job.images.slice(latestImages.length);
          setImages(prev => [...newest, ...prev]);
          latestImages = job.images;
        }
        if (job.usage) setUsage(job.usage);
        if (job.status === 'ready') {
          toast.success(`Generated ${latestImages.length} image${latestImages.length === 1 ? '' : 's'}`);
          refresh();
          return;
        }
        if (job.status === 'failed') {
          const err = job.error || {};
          const message = err.message || 'Something went wrong — try again';
          setErrorMsg(message);
          // Translate error codes into actionable UI hints
          if (err.code === 'service_quota') toast.error('The AI service is at its limit. Retry in a minute.');
          else if (err.code === 'safety_blocked') toast.error('Prompt blocked by the safety filter. Rephrase and retry.');
          else if (err.code === 'service_unavailable') toast.error('Image model unreachable — try again shortly.');
          else toast.error(message);
          return;
        }
      }
      setErrorMsg('Generation is taking longer than usual. Please check "My Creations" in a minute.');
      toast.error('Still working on your image… check back shortly.');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || e.message || 'Something went wrong — try again';
      if (status === 429 && detail?.error === 'quota_exceeded') {
        setErrorMsg(msg);
        toast.error('Daily limit reached');
        if (!user?.is_premium) setShowUpgrade(true);
      } else if (status === 402) {
        setShowUpgrade(true);
      } else {
        setErrorMsg(msg);
        toast.error(msg);
      }
      loadUsage();
    }
    finally { setLoading(false); }
  };

  // Normalize generated images into creation-shape items the lightbox understands
  const lightboxItems = images.map(img => ({
    id: img.id,
    type: 'image',
    url: img.url,
    prompt: img.prompt,
    published: img.published,
    creator: user ? { id: user.id, username: user.username, display_name: user.display_name } : null,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
      <UpgradeDialog open={showUpgrade} onClose={() => setShowUpgrade(false)} feature="image" />
      <Card className="glass p-5 space-y-4 h-fit">
        <div>
          <div className="font-display text-xl font-bold flex items-center gap-2"><Wand2 className="w-5 h-5 text-violet-400" /> Jixxeo Studio</div>
          <p className="text-xs text-slate-400 mt-1">Generate images with AI</p>
        </div>
        <div>
          <Label>Prompt</Label>
          <Textarea data-testid="img-prompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="A cyberpunk city at dusk, neon signs, cinematic" rows={4} />
        </div>
        <div>
          <Label>Negative (optional)</Label>
          <Input data-testid="img-negative" value={negative} onChange={e => setNegative(e.target.value)} placeholder="blurry, low quality" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Aspect</Label>
            <Select value={aspect} onValueChange={setAspect}>
              <SelectTrigger data-testid="img-aspect"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1:1">Square</SelectItem>
                <SelectItem value="16:9">Wide</SelectItem>
                <SelectItem value="9:16">Portrait</SelectItem>
                <SelectItem value="4:3">Classic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Style</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="img-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="photorealistic">Photorealistic</SelectItem>
                <SelectItem value="anime">Anime</SelectItem>
                <SelectItem value="cyberpunk">Cyberpunk</SelectItem>
                <SelectItem value="3d">3D Render</SelectItem>
                <SelectItem value="painting">Painting</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Number of images: {n}</Label>
          <input type="range" min="1" max="4" value={n} onChange={e => setN(+e.target.value)} className="w-full" data-testid="img-count" />
        </div>
        <Button data-testid="img-generate" onClick={generate} disabled={loading} className="w-full gradient-brand text-white">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
            : (!guard.ok && !guardAck) ? <><ShieldAlert className="w-4 h-4 mr-2" /> Review warning & proceed</>
            : <><Wand2 className="w-4 h-4 mr-2" /> Generate</>}
        </Button>

        {!guard.ok && <PromptGuardWarning warnings={guard.warnings} ack={guardAck} onAckChange={setGuardAck} />}

        {errorMsg && (
          <div data-testid="img-error" className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 text-rose-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-rose-100 leading-relaxed">{errorMsg}</div>
          </div>
        )}

        {usage && (
          <div data-testid="img-usage-card" className="rounded-xl bg-slate-900/50 border border-white/10 p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
                <Zap className="w-3.5 h-3.5 text-violet-300" /> Image Generation Usage
              </div>
              <div className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-white/10">
                {usage.tier}
              </div>
            </div>
            <div className="flex items-end justify-between gap-2">
              <div>
                <div className="text-[11px] text-slate-400">
                  <span data-testid="img-usage-used" className="font-bold text-white">{usage.used}</span>
                  {' / '}
                  <span data-testid="img-usage-limit" className="font-bold text-white">{usage.limit}</span>
                  {' images used today'}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  <span data-testid="img-usage-remaining" className="text-emerald-300 font-bold">{usage.remaining}</span> remaining · resets in rolling 24-hour window
                </div>
              </div>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div
                className={`h-full ${usage.remaining > 0 ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500' : 'bg-rose-500'}`}
                style={{ width: `${Math.min(100, (usage.used / Math.max(1, usage.limit)) * 100)}%` }}
              />
            </div>
            {usage.remaining === 0 && (
              <div className="text-[10px] text-rose-200 pt-1">
                You've hit today's limit. {usage.tier === 'free'
                  ? <button onClick={() => setShowUpgrade(true)} className="underline hover:text-rose-100 font-bold">Upgrade to Premium</button>
                  : 'Try again tomorrow.'}
              </div>
            )}
            <div className="text-[10px] text-slate-500 pt-1 border-t border-white/5 mt-1 leading-relaxed">
              <span className="text-slate-300 font-semibold">How limits work:</span> Each member gets a daily image quota —
              {' '}<span className="text-slate-300">Free: 5/day</span>, <span className="text-slate-300">Premium: 100/day</span>.
              It's a sliding 24-hour window counted per account (not per device).
              The quota is set in <span className="font-mono text-slate-300">backend settings</span> (<span className="font-mono">free_daily_image_limit</span> / <span className="font-mono">premium_daily_image_limit</span>) and can be raised from Admin → Settings → Features.
              {' '}Image rendering itself runs on the Universal Key — if the key's budget runs low, the error is "service_quota" and resets within minutes.
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 auto-rows-max">
        {images.length === 0 && (
          <Card className="glass p-10 col-span-full text-center">
            <div className="font-display text-lg font-bold">Your creations will appear here</div>
            <div className="text-sm text-slate-400 mt-1">Describe an image, adjust settings, click Generate</div>
          </Card>
        )}
        {images.map((img, i) => (
          <button
            key={img.id}
            data-testid={`img-${img.id}`}
            onClick={() => setLightboxIdx(i)}
            className="glass overflow-hidden group relative rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400/60 active:scale-[0.99] transition-transform text-left"
            title="Tap to preview, publish, or download"
          >
            <img src={img.url} alt={img.prompt} className="w-full aspect-square object-cover" />
            <div className="p-3">
              <div className="text-xs text-slate-400 line-clamp-2">{img.prompt}</div>
              <div className="text-[10px] text-violet-300 mt-1">Tap to preview →</div>
            </div>
          </button>
        ))}
      </div>

      {lightboxIdx !== null && lightboxItems.length > 0 && (
        <CreationLightbox
          items={lightboxItems}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          showOwnerActions
          onPublish={(id) => {
            setImages(prev => prev.map(x => x.id === id ? { ...x, published: true } : x));
          }}
        />
      )}
    </div>
  );
}
